import React from 'react';

import { HStack, Primitive } from '@rtkwlf/fenrir-react';
import type { ConciergeTier } from '@rtkwlf/track-public';

import { ConciergeTierShield } from '../../icons/concierge-tier-shield';
import { useSecurityJourneyTranslations } from '../../locales/use-security-journey-translations';

import './concierge-tier-badge.css';

type TierProps = {
  tier: ConciergeTier;
};

/* -------------------------------------------------------------------------------------------------
 * Icon
 * -----------------------------------------------------------------------------------------------*/
const Icon: React.FC<TierProps> = ({ tier }) => {
  // registering a unique ID for linearGradient for each instance to avoid conflict
  const id = `${React.useId()}-${tier}`;

  // rendering the raw svg so we can directly control the HTML attributes (namely `fill` on the path)
  // that wouldn't be accessible via the FontAwesomeIcon wrapper component
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="concierge-shield"
      role="img"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <defs>
        <linearGradient id={id}>
          <stop stopColor="var(--start-color)" />
          <stop offset="1" stopColor="var(--end-color)" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${id})`}
        d="M12 19.2188C10.125 18.0625 8.57812 16.4844 7.35938 14.4844C6.17188 12.4531 5.48438 10.125 5.29688 7.5L12 4.6875V19.2188ZM21.8438 3.9375C22.2812 4.09375 22.625 4.35938 22.875 4.73438C23.125 5.10938 23.25 5.53125 23.25 6C23.25 8.4375 22.9062 10.7344 22.2188 12.8906C21.5312 15.0156 20.6406 16.7969 19.5469 18.2344C18.4531 19.6406 17.3438 20.8125 16.2188 21.75C15.0938 22.6875 13.9688 23.375 12.8438 23.8125C12.2812 24.0625 11.7188 24.0625 11.1562 23.8125C9.84375 23.2812 8.57812 22.4844 7.35938 21.4219C6.17188 20.3906 5.07812 19.1406 4.07812 17.6719C3.07812 16.1719 2.26562 14.4062 1.64062 12.375C1.04688 10.3438 0.75 8.21875 0.75 6C0.75 5.53125 0.875 5.10938 1.125 4.73438C1.375 4.35938 1.71875 4.09375 2.15625 3.9375L11.1562 0.1875C11.4062 0.0625 11.6875 0 12 0C12.3125 0 12.5938 0.0625 12.8438 0.1875L21.8438 3.9375ZM12 21.75C14.4062 20.75 16.5 18.8281 18.2812 15.9844C20.0938 13.1406 21 9.8125 21 6L12 2.25L3 6C3 9.75 3.875 13.0625 5.625 15.9375C7.40625 18.7812 9.53125 20.7188 12 21.75Z"
      ></path>
    </svg>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Organization - comes with a background, typically used to describe tiers of organizations
 * -----------------------------------------------------------------------------------------------*/
const Organization: React.FC<TierProps> = ({ tier }) => {
  const { t } = useSecurityJourneyTranslations();
  return (
    <div
      className="concierge-tier-badge-organization light tier-gradient"
      data-testid="conciergeTierBadge"
      data-tier={tier}
    >
      <HStack className="tier-badge" gap="xsmall">
        <ConciergeTierShield />
        <div>
          {t([
            `common.concierge.tier.${tier}`,
            // a paranoid fallback
            'common.concierge.tier.unknown',
          ]).toLocaleUpperCase()}
        </div>
      </HStack>
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Spidr - comes without background, typically used to describe tiers associated with SPiDRs
 * -----------------------------------------------------------------------------------------------*/
const Spidr: React.FC<TierProps> = ({ tier }) => {
  const { t } = useSecurityJourneyTranslations();
  return (
    <Primitive.div
      display="flex"
      gap="small"
      alignItems="center"
      className="concierge-tier-badge-spidr light tier-gradient"
      data-testid="concierge-tier-badge-spidr"
      data-tier={tier}
    >
      <Icon tier={tier} />
      <div>
        {t([
          `common.concierge.tier.${tier}`,
          // a paranoid fallback
          'common.concierge.tier.unknown',
        ])}
      </div>
    </Primitive.div>
  );
};

export const ConciergeTierBadge = {
  Organization,
  Spidr,
};
